import React, { useState, useEffect } from "react";
import { Search, ChevronDown, ChevronUp, Film, Clock, Calendar, User, BarChart } from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

const PlaybackStatsPanel = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generalStats, setGeneralStats] = useState({
    totalViews: 0,
    completedViews: 0,
    completionRate: 0,
    totalWatchHours: 0
  });
  const [movieStats, setMovieStats] = useState([]);
  const [activeUsers, setActiveUsers] = useState([]);
  const [moviesError, setMoviesError] = useState(null);
  const [usersError, setUsersError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedMovie, setExpandedMovie] = useState(null);
  const [filter, setFilter] = useState("all"); // all, today, week, month
  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("token");
        
        // Construir parámetros de consulta para filtrado por fecha
        let params = new URLSearchParams();
        if (filter !== 'all' || dateRange.startDate) {
          if (filter === 'today') {
            const today = new Date().toISOString().split('T')[0];
            params.append('startDate', today);
            params.append('endDate', today);
          } else if (filter === 'week') {
            const today = new Date();
            const lastWeek = new Date(today);
            lastWeek.setDate(lastWeek.getDate() - 7);
            params.append('startDate', lastWeek.toISOString().split('T')[0]);
            params.append('endDate', today.toISOString().split('T')[0]);
          } else if (filter === 'month') {
            const today = new Date();
            const lastMonth = new Date(today);
            lastMonth.setMonth(lastMonth.getMonth() - 1);
            params.append('startDate', lastMonth.toISOString().split('T')[0]);
            params.append('endDate', today.toISOString().split('T')[0]);
          } else if (dateRange.startDate && dateRange.endDate) {
            params.append('startDate', dateRange.startDate);
            params.append('endDate', dateRange.endDate);
          }
        }
        
        const queryParams = params.toString() ? `?${params.toString()}` : '';
        
        // Hacer las solicitudes en paralelo
        const [statsResponse, moviesResponse, usersResponse] = await Promise.all([
          axios.get(`${API_URL}/api/v1/movie-views/statistics${queryParams}`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API_URL}/api/v1/movie-views/most-viewed-movies${queryParams}`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API_URL}/api/v1/movie-views/most-active-users${queryParams}`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
        
        // Procesar datos de estadísticas generales
        const statsData = statsResponse.data?.data || statsResponse.data;
        setGeneralStats({
          totalViews: statsData.totalViews || 0,
          completedViews: statsData.completedViews || 0,
          completionRate: statsData.completionRate || 0,
          totalWatchHours: statsData.totalWatchHours || 0
        });
        
        // Procesar datos de películas
        const moviesData = moviesResponse.data?.data || moviesResponse.data || [];
        console.log("Datos de películas recibidos:", moviesData); // Depuración
        
        // Procesar y mapear correctamente los nombres de campos del backend
        const processedMovies = Array.isArray(moviesData) ? moviesData.map(movie => {
          return {
            movieId: movie.movieId,
            // Usar el nombre de la película desde la relación movie.name
            title: movie.movie?.name || `Película ID: ${movie.movieId}`,
            // Usar la duración desde movie.runtime (en segundos)
            duration: movie.movie?.runtime || 0,
            viewCount: parseInt(movie.viewCount) || 0,
            avgPercentageWatched: parseFloat(movie.avgPercentageWatched) || 0,
            totalWatchTime: parseFloat(movie.totalWatchTime) || 0,
            // Mantener cualquier otro dato que venga del backend
            ...movie
          };
        }) : [];
        
        setMovieStats(processedMovies);
        
        // Procesar datos de usuarios
        const usersData = usersResponse.data?.data || usersResponse.data || [];
        console.log("Datos de usuarios recibidos:", usersData); // Depuración
        
        // Asegurarse de que todos los campos necesarios estén presentes
        const processedUsers = Array.isArray(usersData) ? usersData.map(user => {
          // Depuración temporal
          console.log("Usuario procesando:", user);
          
          return {
            userId: user.userId,
            userName: user.userName || user.user?.name || `Usuario ${user.userId}`,
            userEmail: user.userEmail || user.user?.email || "",
            uniqueMovies: parseInt(user.uniqueMoviesWatched) || 0, // Películas únicas 
            moviesWatched: parseInt(user.moviesWatched) || 0, // Total de vistas
            totalWatchTime: parseFloat(user.totalWatchTime) || 0,
            totalWatchTimeFormatted: user.totalWatchTimeFormatted || formatTime(user.totalWatchTime || 0),
            avgPercentageWatched: parseFloat(user.avgPercentageWatched || 0).toFixed(1)
          };
        }) : [];
        
        setActiveUsers(processedUsers);
        
      } catch (err) {
        console.error("Error al cargar estadísticas de reproducción:", err.response || err);
        setError(
          err.response?.data?.message || 
          `Error al cargar los datos (${err.message}). Por favor, verifica que estés conectado y tengas permisos de administrador.`
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [filter, dateRange]);

  const toggleExpand = (movieId) => {
    setExpandedMovie(expandedMovie === movieId ? null : movieId);
  };

  // Filtrar películas por el término de búsqueda
  const filteredMovies = searchTerm
    ? movieStats.filter(
        (movie) =>
          movie.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          movie.movieId?.toString().includes(searchTerm)
      )
    : movieStats;

  // Función para formatear tiempo en hh:mm:ss
  const formatTime = (seconds) => {
    if (!seconds) return "00:00:00";
    
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    return [hrs, mins, secs]
      .map(val => val.toString().padStart(2, "0"))
      .join(":");
  };

  // Función para formatear porcentaje
  const formatPercentage = (value) => {
    if (value === undefined || value === null) return "0%";
    return `${Math.round(value)}%`;
  };

  // Función para formatear fecha
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString() + " " + date.toLocaleTimeString();
  };

  if (loading) {
    return (
      <div className="w-full flex justify-center p-8">
        <div className="animate-spin h-12 w-12 border-t-4 border-blue-500 border-solid rounded-full"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 text-red-600 p-4 rounded-lg mb-8">
        <p className="font-medium">Error</p>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Header y controles */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-8">
        <h2 className="text-2xl font-bold text-white">Estadísticas de Reproducción</h2>
        
        <div className="flex flex-col md:flex-row gap-3">
          {/* Filtro por fecha */}
          <div className="flex flex-wrap gap-2">
            <button 
              onClick={() => setFilter("all")} 
              className={`py-2 px-4 rounded-full text-sm ${filter === "all" ? "bg-blue-600 text-white" : "bg-gray-800 text-gray-300"}`}
            >
              Todos
            </button>
            <button 
              onClick={() => setFilter("today")} 
              className={`py-2 px-4 rounded-full text-sm ${filter === "today" ? "bg-blue-600 text-white" : "bg-gray-800 text-gray-300"}`}
            >
              Hoy
            </button>
            <button 
              onClick={() => setFilter("week")} 
              className={`py-2 px-4 rounded-full text-sm ${filter === "week" ? "bg-blue-600 text-white" : "bg-gray-800 text-gray-300"}`}
            >
              Última semana
            </button>
            <button 
              onClick={() => setFilter("month")} 
              className={`py-2 px-4 rounded-full text-sm ${filter === "month" ? "bg-blue-600 text-white" : "bg-gray-800 text-gray-300"}`}
            >
              Último mes
            </button>
          </div>
          
          {/* Búsqueda */}
          <div className="relative">
            <input
              type="text"
              placeholder="Buscar película..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full md:w-72 py-2 px-4 pl-10 bg-gray-800 border border-gray-700 rounded-full text-white"
            />
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={18} />
          </div>
        </div>
      </div>
      
      {/* Selector de rango personalizado */}
      {filter === "custom" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-gray-400 mb-1">Fecha inicio:</label>
            <input
              type="date"
              className="w-full py-2 px-4 bg-gray-800 border border-gray-700 rounded-lg text-white"
              value={dateRange.startDate || ""}
              onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-gray-400 mb-1">Fecha fin:</label>
            <input
              type="date"
              className="w-full py-2 px-4 bg-gray-800 border border-gray-700 rounded-lg text-white"
              value={dateRange.endDate || ""}
              onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
            />
          </div>
        </div>
      )}
      
      {/* Contador de resultados */}
      <div className="text-gray-400 mb-6">
        Mostrando {filteredMovies.length} películas con datos de reproducción
      </div>

      {/* Tarjetas de estadísticas generales */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-gray-800 rounded-xl p-6 shadow-lg">
          <div className="text-2xl text-blue-400 mb-2 flex items-center">
            <Film className="mr-2" />
            Total Reproducciones
          </div>
          <div className="text-4xl font-bold text-white">
            {generalStats.totalViews}
          </div>
        </div>
        <div className="bg-gray-800 rounded-xl p-6 shadow-lg">
          <div className="text-2xl text-blue-400 mb-2 flex items-center">
            <Clock className="mr-2" />
            Tiempo Total Visto
          </div>
          <div className="text-4xl font-bold text-white">
            {formatTime(generalStats.totalWatchHours * 3600)}
          </div>
        </div>
        <div className="bg-gray-800 rounded-xl p-6 shadow-lg">
          <div className="text-2xl text-blue-400 mb-2 flex items-center">
            <Calendar className="mr-2" />
            Videos Completados
          </div>
          <div className="text-4xl font-bold text-white">
            {generalStats.completedViews} 
            <span className="text-gray-400 text-lg ml-2">
              ({formatPercentage(generalStats.completionRate)})
            </span>
          </div>
        </div>
      </div>
      
      <div className="flex flex-col lg:flex-row gap-6 mb-8">
        {/* Sección de películas más vistas */}
        <div className="w-full lg:w-2/3">
          <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
            <BarChart size={20} className="mr-2 text-blue-400" />
            Películas Más Vistas
          </h3>
          
          {filteredMovies.length === 0 ? (
            <div className="bg-gray-800 rounded-xl p-8 text-center flex flex-col items-center">
              <Film size={48} className="text-gray-600 mb-4" />
              <p className="text-gray-400">No hay datos de reproducción disponibles con los filtros actuales.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredMovies.map((movie) => (
                <div
                  key={movie.movieId}
                  className="bg-gray-800 rounded-xl overflow-hidden transition-shadow duration-300 hover:shadow-lg"
                >
                  {/* Cabecera de película */}
                  <div 
                    className="p-4 flex justify-between items-center cursor-pointer hover:bg-gray-750"
                    onClick={() => toggleExpand(movie.movieId)}
                  >
                    <div className="flex items-center gap-3">
                      <Film size={24} className="text-blue-400" />
                      <div>
                        <h3 className="font-medium text-white">{movie.title || `Película ID: ${movie.movieId}`}</h3>
                        <p className="text-gray-400 text-sm">Duración: {formatTime(movie.duration || 0)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-sm text-gray-400">Vistas</p>
                        <p className="font-semibold text-white">{movie.viewCount || 0}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-400">% Promedio</p>
                        <p className="font-semibold text-white">{formatPercentage(movie.avgPercentageWatched || 0)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-400">Tiempo</p>
                        <p className="font-semibold text-white">{formatTime((movie.totalWatchTime || 0) / movie.viewCount)}</p>
                      </div>
                      
                    </div>
                  </div>

                  
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Sección de usuarios más activos */}
        <div className="w-full lg:w-1/3">
          <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
            <User size={20} className="mr-2 text-blue-400" />
            Usuarios Más Activos
          </h3>
          
          {activeUsers.length === 0 ? (
            <div className="bg-gray-800 rounded-xl p-8 text-center flex flex-col items-center">
              <User size={48} className="text-gray-600 mb-4" />
              <p className="text-gray-400">No hay datos de usuarios disponibles con los filtros actuales.</p>
            </div>
          ) : (
            <div className="bg-gray-800 rounded-xl overflow-hidden">
              <div className="p-4">
                <table className="min-w-full">
                  <thead>
                    <tr>
                      <th className="pb-3 text-left text-xs text-gray-400 font-medium">Usuario</th>
                      <th className="pb-3 text-left text-xs text-gray-400 font-medium">Películas vistas</th>
                      <th className="pb-3 text-right text-xs text-gray-400 font-medium">Tiempo visto</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {activeUsers.map((user) => (
                      <tr key={user.userId} className="hover:bg-gray-750">
                        <td className="py-3 text-sm">
                          <div className="text-white">{user.userName || `Usuario ${user.userId}`}</div>
                          <div className="text-gray-400 text-xs">{user.userEmail || ''}</div>
                        </td>
                        <td className="py-3 text-sm">
                          <div className="text-white">{user.uniqueMovies || 0} / {user.moviesWatched || 0}</div>
                          <div className="text-gray-400 text-xs">
                            {user.uniqueMovies === 1 ? "película única" : "películas únicas"} / vistas totales
                          </div>
                        </td>
                        <td className="py-3 text-sm text-right">
                          <div className="text-white">{user.totalWatchTimeFormatted || formatTime(user.totalWatchTime || 0)}</div>
                          <div className="text-gray-400 text-xs">{user.avgPercentageWatched || 0}% promedio</div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlaybackStatsPanel;
